'use client';

import { Config } from 'constants/config';
import { CmsFeaturesProvider } from '@hyphengroup/cms-components/components/CmsFeaturesProvider';
import type { TravelInsuranceQuotesData } from '@hyphengroup/cms-components/types/travelInsurance';
import {
  CmsClientProvider,
  useCmsClientContext,
} from '@hyphengroup/cms-components/utils/cmsPageContext';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { CmsArticle, CmsPage } from '../types';
import { CmsSettings } from '../types/settings';
import { PageData } from './types';
import { PageCalculationsProvider } from '$/(cms)/_modules/loanScope/PageCalculationsProvider';
import { PageType } from '$/components/cms/types';

export type PageDataProviderProps<T extends CmsPage | CmsArticle> = {
  pageData: PageData<T>;
  pageType: PageType;
  publicCmsConfig: PublicCmsConfig;
  isPreview: boolean;
  /**
   * Site settings as fetched for the page (logo, disclaimer, socials …). Carried
   * in context so components mounted below — the email-capture modal, for one —
   * do not have to be threaded props for it.
   */
  settings?: CmsSettings;
  promotionCampaign?: {
    activePromotionIndex: number;
  };
};

// Extended value stored in context (adds a setter for promotion index)
export type PageDataContextValue = PageDataProviderProps<
  CmsPage | CmsArticle
> & {
  setActivePromotionIndex: (index: number) => void;
  setTravelQuotes: (
    props: Omit<
      Required<PageData<CmsPage>>['travelQuotes'],
      'isBackendData' | 'countries' | 'regions'
    >,
  ) => void;
  // Sets (or clears with `undefined`) the M3 plan-tier selection. Thin setter
  // that merges only `selectedTier` into the existing `travelQuotes` slice, so
  // a click does not have to round-trip the whole quotes payload.
  setSelectedTier: (selectedTier: string | undefined) => void;
  // The TI default tier pinned once a search settles (CMS-2054, AC 3.5): the
  // plan shown without a click keeps its place through a re-search. Kept apart
  // from `selectedTier` so a default never reads as the user's pick.
  pinnedDefaultTier: string | undefined;
  setPinnedDefaultTier: (tierId: string | undefined) => void;
  // Publishes a fresh poll tick into ONE module's quote slice (CMS-1896): the
  // client-side continuation of a quote the SSR pass left `inprogress`.
  // Countries/regions are carried over from the SSR slice; a module with no SSR
  // slice is a no-op, since there is nothing the poll could be continuing.
  setModuleTravelQuotes: (
    moduleId: string,
    quotes: Pick<
      Required<PageData<CmsPage>>['travelQuotes'],
      'premiums' | 'status' | 'params'
    >,
  ) => void;
  // Publishes the client-resolved TI purchase-success state (CMS-1794) into the
  // page context so the confirmation blocks read it: the Policy Summary Card
  // wrapper for its rows, and the Progress Stepper wrapper for the greeting it
  // selects and interpolates. Replaces the whole slice (computed atomically).
  setPolicySummary: (
    policySummary: Required<PageData<CmsPage>>['policySummary'],
  ) => void;
};

const PageDataContext = createContext<PageDataContextValue | null>(null);

/** A poll tick merged into the quote slice: what the tick does not carry is kept from the slice it replaces. */
export const mergeTravelQuotes = (
  prev: TravelInsuranceQuotesData | undefined,
  next: Parameters<PageDataContextValue['setTravelQuotes']>[0],
): TravelInsuranceQuotesData => ({
  ...next,
  providerSettings: prev?.providerSettings,
  isBackendData: false,
  countries: prev?.countries ?? [],
  regions: prev?.regions ?? [],
  oldPremiumsIds: next.oldPremiumsIds ?? prev?.oldPremiumsIds ?? [],
  // Preserve the M3 tier selection across premium refreshes (re-search): callers like
  // useTravelSearchPoll don't pass it.
  selectedTier: next.selectedTier ?? prev?.selectedTier,
});

export const PageDataProvider: React.FC<
  PropsWithChildren<PageDataProviderProps<CmsPage | CmsArticle>>
> = ({ children, ...pageData }) => {
  const [context, setContext] = useState(pageData);
  const [pinnedDefaultTier, setPinnedDefaultTier] = useState<string>();
  const outerCmsClientContext = useCmsClientContext();

  const handleSetActivePromotionIndex = useCallback(
    (index: number) => {
      setContext((prev) => ({
        ...prev,
        promotionCampaign: {
          ...prev.promotionCampaign,
          activePromotionIndex: index,
        },
      }));
    },
    [setContext],
  );

  const handleSetTravelQuotes = useCallback<
    PageDataContextValue['setTravelQuotes']
  >(
    (props) => {
      setContext((prev) => ({
        ...prev,
        pageData: {
          ...prev.pageData,
          travelQuotes: mergeTravelQuotes(prev.pageData.travelQuotes, props),
        },
      }));
    },
    [setContext],
  );

  const handleSetModuleTravelQuotes = useCallback<
    PageDataContextValue['setModuleTravelQuotes']
  >(
    (moduleId, quotes) => {
      setContext((prev) => {
        const existing = prev.pageData.moduleTravelQuotes?.[moduleId];
        // The poll only continues a quote the SSR pass started; without an SSR
        // slice there are no countries/regions to carry and nothing to update.
        if (!existing) {
          return prev;
        }

        return {
          ...prev,
          pageData: {
            ...prev.pageData,
            moduleTravelQuotes: {
              ...prev.pageData.moduleTravelQuotes,
              [moduleId]: {
                ...existing,
                ...quotes,
                isBackendData: false,
              },
            },
          },
        };
      });
    },
    [setContext],
  );

  const handleSetSelectedTier = useCallback<
    PageDataContextValue['setSelectedTier']
  >(
    (selectedTier) => {
      setContext((prev) => {
        // The tier selector only renders on TI provider pages, where the
        // travelQuotes slice is always populated. Guard so we never fabricate
        // an invalid slice missing its required fields.
        if (!prev.pageData.travelQuotes) {
          return prev;
        }

        return {
          ...prev,
          pageData: {
            ...prev.pageData,
            travelQuotes: {
              ...prev.pageData.travelQuotes,
              selectedTier,
            },
          },
        };
      });
    },
    [setContext],
  );

  const handleSetPolicySummary = useCallback<
    PageDataContextValue['setPolicySummary']
  >(
    (policySummary) => {
      setContext((prev) => ({
        ...prev,
        pageData: {
          ...prev.pageData,
          policySummary,
        },
      }));
    },
    [setContext],
  );

  const value: PageDataContextValue = useMemo(
    () => ({
      ...context,
      setActivePromotionIndex: handleSetActivePromotionIndex,
      setTravelQuotes: handleSetTravelQuotes,
      setSelectedTier: handleSetSelectedTier,
      pinnedDefaultTier,
      setPinnedDefaultTier,
      setModuleTravelQuotes: handleSetModuleTravelQuotes,
      setPolicySummary: handleSetPolicySummary,
    }),
    [
      context,
      handleSetActivePromotionIndex,
      handleSetTravelQuotes,
      handleSetSelectedTier,
      pinnedDefaultTier,
      handleSetModuleTravelQuotes,
      handleSetPolicySummary,
    ],
  );

  // Re-provide CmsClientContext with page-level fields so cms-components
  // (`useCtaUrl`, `<ApplyCtaLink>`) auto-pull vertical / pageType without every
  // consumer threading them as props. Outer app-level CmsClientProvider
  // supplies brand/lang/etc; we merge those with per-page values here.
  // `loanParams` is intentionally not added — see comment in
  // `CmsClientContextValue`.
  // Depends on the page SECTION, not on the whole `pageData`: a published quote
  // tick replaces that object, and depending on it would rebuild this value and
  // re-render every `CmsClientContext` consumer on the page - the re-render the
  // separate calculation context exists to avoid.
  const vertical = context.pageData.page.section;
  const cmsClientValue = useMemo(
    () => ({
      ...outerCmsClientContext,
      vertical,
      pageType: context.pageType,
    }),
    [outerCmsClientContext, vertical, context.pageType],
  );

  return (
    <CmsClientProvider value={cmsClientValue}>
      <PageDataContext.Provider value={value}>
        {/* The calculation store for every block on the page, kept in step
              with the reader's filter (CMS-1890). */}
        <PageCalculationsProvider>
          {/* One mount point for the client-side feature providers
                cms-components owns (email capture, …). New features are added
                inside it, so no page container has to be touched again. */}
          <CmsFeaturesProvider>{children}</CmsFeaturesProvider>
        </PageCalculationsProvider>
      </PageDataContext.Provider>
    </CmsClientProvider>
  );
};

export const usePageDataContext = () => {
  const context = useContext(PageDataContext);
  if (!context) {
    throw new Error('usePageData must be used within a PageDataProvider');
  }
  return context;
};

/**
 * Lenient variant for code shared with routes that have no PageDataProvider
 * (apply-v3): returns null instead of throwing (CMS-1924).
 */
export const usePageDataContextOptional = () => useContext(PageDataContext);

// only props that can be sent to the client (no tokens!)
export type PublicCmsConfig = Pick<
  Config['cms'],
  'productDetailType' | 'plTenure'
> & {
  chcBaseUrl: string | undefined;
};
